import { useState } from "react";
import { Label } from "../../label";
import { Input } from "../../input";

export interface FormResponsesContentProps {
  formTitle?: string;
  responses?: Array<{
    id: string;
    responderName: string;
    isAdmin: boolean;
    date: string;
    status?: "completed" | "draft";
  }>;
  onResponseSelect?: (response: {
    id: string;
    responderName: string;
    isAdmin: boolean;
    date: string;
    status?: "completed" | "draft";
  }) => void;
}

export function FormResponsesContent({ responses = [], onResponseSelect }: FormResponsesContentProps) {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredResponses = responses.filter((response) => {
    const searchLower = searchTerm.toLowerCase();
    const nameLower = response.responderName.toLowerCase();
    const dateLower = response.date.toLowerCase();

    // Check name match
    if (nameLower.includes(searchLower)) {
      return true;
    }

    // Check date match - more flexible date searching
    if (dateLower.includes(searchLower)) {
      return true;
    }

    // Additional date format matching
    const dateStr = response.date;

    // Extract date parts for more flexible matching
    // Handle formats like "12/15/2024 2:30 PM"
    const dateMatch = dateStr.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (dateMatch) {
      const month = dateMatch[1];
      const day = dateMatch[2];
      const year = dateMatch[3];

      if (month && day && year) {
        // Check various date format matches
        const formats = [
          `${month}/${day}`, // "12/15"
          `${month}/${day}/${year}`, // "12/15/2024"
          `${month}/${day}/${year.slice(-2)}`, // "12/15/24"
          month, // "12"
          day, // "15"
          year, // "2024"
          year.slice(-2), // "24"
        ];

        if (formats.some((format) => format.includes(searchLower))) {
          return true;
        }
      }
    }

    return false;
  });

  return (
    <div className="flex flex-col gap-4 h-full p-6">
      <div className="space-y-2">
        <Label className="text-sm font-medium">Select Response</Label>
        <Input
          id="responses-search"
          placeholder="Search by responder name or date..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="flex flex-col gap-3 overflow-auto p-1">
        {filteredResponses.map((response) => (
          <div
            key={response.id}
            className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer bg-white"
            onClick={() => onResponseSelect?.(response)}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="font-medium text-sm">
                  {response.isAdmin ? `${response.responderName} (Admin)` : response.responderName}
                </div>
                <div className="text-xs text-muted-foreground mt-1">{response.date}</div>
              </div>
            </div>
          </div>
        ))}

        {filteredResponses.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            {searchTerm ? "No responses found matching your search." : "No responses yet."}
          </div>
        )}
      </div>
    </div>
  );
}
